<template>
  <div class="profile-page">
    <user-profile-picture
      :url="bannerPic"
      :isMe="isMe"
      imageType="banner"
      @change="loadUserProfile() && getLoggedInUserProfile()">
    </user-profile-picture>
    <div class="profile-content">
      <div class="profile-container">
        <div class="profile-image-container">
          <user-profile-picture
            :url="profilePic"
            :isMe="isMe"
            imageType="profile"
            @change="loadUserProfile() && getLoggedInUserProfile()">
          </user-profile-picture>
        </div>
        <router-link to="/account/edit" class="edit-container" v-if="isMe">
          <font-awesome-icon :icon="['fa', 'pencil-alt']" size="xs">
          </font-awesome-icon>
          <span>EDIT</span>
        </router-link>
        <div :class="{'my-profile-portfolio': isMe, 'profile-portfolio': !isMe}">
          <div class="profile-name">{{displayName}}
            <div class="tooltip">
              <font-awesome-layers
                v-if="isVerified"
                data-tooltip="Verified Account" class="tooltip">
                <font-awesome-icon :icon="['fa', 'circle']"
                :style="{ color: '#00c0ff', fontSize: '1rem' }"/>
                <font-awesome-icon :icon="['fa', 'check']"
                :style="{ color: 'white', fontSize: '0.5rem'}"/>
              </font-awesome-layers>
              <span class="tooltiptext">Linked Account</span>
            </div>
          </div>
          <div class="social-container">
            <contact-user :receiverUserName="$route.params.userName" class="contact-user"
              :receiverDisplayName="displayName" v-if="!isMe" />
            <share-url :url="this.$route.path"
              :title="`Check out ${displayName}'s profile on YouPaired`"/>
          </div>
          <div class="profile-company">{{companyName}}</div>
          <div class="profile-location" v-if="location && location.title">
            <span>{{location.title}} {{location.vicinity}}</span>
          </div>
          <links-view-container :value="websites.map(website => ({ value: website }))"
            class="social-buttons"></links-view-container>
          <div class="profile-description">
            <div class="profile-about">
              {{about}}
            </div>
          </div>
        </div>
        <!-- TABS IN PROFILE PAGE ARE UNUSED FOR NOW. ONLY EVENTS ARE SHOWN -->
        <div class="profile-tabs" v-if="false">
          <ul class="nav-pills" >
          <router-link :to="{ name: 'user-profile-events' }" class="nav-link"
            :class="{ 'router-link-active' : $route.name === 'user-profile-events' }">
            <font-awesome-icon :icon="['fa', 'calendar-day']" size="2x"
            class="calendar-icon">
            </font-awesome-icon>
            <span>Events</span>
          </router-link>
          <router-link :to="{ name: 'user-profile-deals' }" class="nav-link">
            <font-awesome-icon :icon="['fa', 'handshake']" size="2x" class="handshake-icon">
            </font-awesome-icon>
            <span>Deals</span>
          </router-link>
          <router-link :to="{ name: 'user-profile-reviews' }" class="nav-link">
            <font-awesome-icon :icon="['fa', 'star']" size="2x" class="star-icon">
            </font-awesome-icon>
            <span>Reviews</span>
          </router-link>
          </ul>
        </div>
        <div class="content-card">
          <router-view :events="events"></router-view>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import UserProfilePicture from '@/components/user-profile/UserProfilePicture.vue';
import LinksViewContainer from '@/components/utils/LinksViewContainer.vue';

export default {
  name: 'profile',
  components: {
    UserProfilePicture,
    LinksViewContainer,
  },
  data() {
    return {
      displayName: null,
      profilePic: null,
      bannerPic: null,
      about: null,
      location: null,
      companyName: null,
      websites: [],
      linkedNetworks: [],
      isVerified: false,
      events: [],
    };
  },
  // called when switching from one user profile to another user profile
  beforeRouteUpdate(to, from, next) {
    next();
    this.loadUserProfile();
  },
  created() {
    this.loadUserProfile();
  },
  computed: {
    ...mapState('user', ['userName']),
    ...mapState('auth', ['socialNetworks']),
    isMe() {
      return this.$route.params.userName === this.userName;
    },
    linkedSocialNetworks() {
      const ret = [];
      this.socialNetworks.forEach((socialNetwork) => {
        const { id } = socialNetwork;
        const linkedNetwork = this.linkedNetworks
          .find(({ connection }) => connection === id);
        if (linkedNetwork) {
          ret.push({
            ...socialNetwork,
            link: linkedNetwork.link,
          });
        }
      });
      return ret;
    },
  },
  methods: {
    ...mapActions('user', ['getProfile', 'getLoggedInUserProfile']),
    async loadUserProfile() {
      try {
        const profileData = await this.getProfile(this.$route.params.userName);
        this.displayName = profileData.displayName;
        this.profilePic = profileData.profilePic;
        this.bannerPic = profileData.bannerPic;
        this.about = profileData.about;
        this.location = profileData.location ? profileData.location.title : '-';
        this.companyName = profileData.companyName;
        this.websites = profileData.websites;
        this.events = profileData.events;
        this.linkedNetworks = profileData.linkedNetworks;
        this.isVerified = profileData.isVerified;
      } catch (err) {
        if (err.response.status === 404) {
          this.$router.replace({ name: 'page-not-found' });
        } else {
          this.$toasted.show(err.response.data, {
            type: 'info',
            duration: 5000,
          });
          this.$router.push({ name: 'home' });
        }
      }
    },
  },
};

</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';

.profile-container {
  color: $user-profile-font;
  background-color: transparent;
  text-align: center;
}

.profile-image-container {
  position: absolute;
  top: -5rem;
  left: 0;
  right: 0;
  margin: 0 auto;
}

.profile-portfolio {
  padding-top: 6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.my-profile-portfolio {
  padding-top: 10rem;
}

.profile-content {
  margin-bottom: 2rem;
  @media (min-width: 768px) {
    margin-left: 4rem;
    margin-right: 4rem;
    margin-top: -10rem;
  }
  border-radius: 0.1rem;
  box-shadow: 0 1px 2px rgba(73, 73, 73, 0.5);
  background: #FFF;
  position: relative;
  z-index: 1;
}

.profile-name {
  font-size: 1.75rem;
  margin: 1rem 0 1rem 0;
}

.profile-company {
  font-size: 1rem;
  margin: 1rem 0;
  color: $user-profile-sub;
}

.profile-location {
  font-size: 1rem;
  font-weight: 500;
  margin: 0.5rem 0;
  color: $user-profile-sub;
  padding: 1rem;
}

.social-buttons {
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0.5rem 0;
}

.social-buttons > * {
  padding: 0.75rem;
  cursor: pointer;
  &:hover {
    filter: brightness(0.5);
    -webkit-filter: brightness(0.5);
  }
}

.calendar-icon {
  color: #555;
}

.handshake-icon {
  color: #555;
}

.star-icon {
  color: #555;
}

.facebook-icon {
  color: $facebook;
}

.google-icon {
  color: $google;
}

.twitter-icon {
  color: $twitter;
}

.instagram-icon {
  color: $instagram;
}

.twitch-icon {
  color: $twitch;
}

.linkedin-icon {
  color: $linkedin;
}

.profile-about {
  margin: 1rem auto 0;
  font-size: 1rem;
  max-width: 600px;
  font-weight: 500;
  text-align: justify;
  text-align-last: left;
  color: $user-profile-sub;
  padding: 1rem;
}

.profile-tabs {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.nav-pills {
  position: relative;
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  cursor: pointer;
  list-style-type: none;
  padding: 0;
}

.nav-pills ul {
 text-decoration: none;
}

.nav-link {
  line-height: 3rem;
  font-weight: 500;
  padding: 0.75rem 1rem 0rem 1rem;
  text-align: justify;
  align-items:center;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  margin: 1rem;
  &:hover {
    background-color: rgba(200,200,200,.2);
  }
  width: 2rem;
  border-radius: 0.2rem;
  color: $user-profile-font;
}

.router-link-active {
  background-color: $user-profile-tab;
  align-items:center;
  color: #ffffff;
  font-weight: bold;
  &:hover {
    background-color: $user-profile-tab;
  }
}

.router-link-active svg {
  color: white;
}

.content-card {
  margin: 1.5rem 0;
}

.edit-container {
  position: absolute;
  right: 1rem;
  top: 1rem;
  height: 2rem;
  line-height: 2rem;
  white-space: nowrap;
  padding: 0 1rem;
  &:hover{
    background-color: $login-hover;
  }
}

.edit-container > span {
  padding: 0.35rem;
}

.edit-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  box-shadow: 0 4px 6px rgba(50,50,93,.11), 0 1px 3px rgba(0,0,0,.08);
  background: $login-background;
  border-radius: 0.2rem;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: .025em;
  color: white;
  text-decoration: none;
  cursor: pointer;
}

.social-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
}

.contact-user {
  margin-right: 1rem;
}

.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip .tooltiptext {
  visibility: hidden;
  width: 6rem;
  background-color: #e4e4e4;
  color: #444444;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  font-size: 0.75rem;
  position: absolute;
  z-index: 1;
}

.tooltip:hover .tooltiptext {
  visibility: visible;
}
</style>

